/*
 * Behavior: scrolling looks synthetic. Records scroll samples (position + time)
 * and checks two traits from lib/bots/scrollBehavior:
 *   - uniform scroll: every step moves by an almost identical amount AND is
 *     spaced almost identically in time (a real mouse wheel has even sizes but
 *     uneven timing, so it is not flagged);
 *   - large jump: a single scroll moves further than a human input device could.
 * After a watch window we also flag the odd case where the scroll position moved
 * but no scroll event ever fired.
 *
 * False-positive guards (a large jump is NOT counted when the scroll is clearly
 * user-driven):
 *   - touch / fling scrolling;
 *   - a mouse button held down (scrollbar drag, text-selection auto-scroll);
 *   - just after a scrolling key (End, Home, PageUp/Down, Space, arrows);
 *   - just after a focus change (the browser auto-scrolls to the focused element).
 * Programmatic scrolls are marked and skipped: window.scrollTo / window.scrollBy
 * and Element.scrollIntoView are wrapped, and in-page anchor / hash-link jumps
 * (hashchange + '#' link clicks) are marked too. Reports via the shared callback
 * from useBotDetection.
 */
import { useEffect, useRef } from 'react'
import { hasLargeScrollJump, hasUniformScroll, ScrollSample } from 'lib/bots/scrollBehavior'
import { markProgrammaticScroll, isProgrammaticScrollActive } from 'lib/bots/programmaticScroll'
import { TrackBotTrafficAction } from 'lib/bots/trackBotTraffic'

// How long (in milliseconds) to watch before checking for a position change
// that happened with no scroll events.
const WATCH_DURATION_MS = 12000
// Most scroll samples to keep (a count); the oldest is dropped once we pass this.
const MAX_SCROLL_SAMPLES = 500
// Momentum scrolling keeps firing scroll events for a while after the finger
// lifts; treat scrolls within this many milliseconds of a touch as touch-driven.
const FLING_SETTLE_MS = 2000
// After a scrolling key or a focus change, ignore large jumps for this long.
const USER_JUMP_GRACE_MS = 1000
// Keys that make the page jump on their own.
const SCROLL_KEYS = ['End', 'Home', 'PageUp', 'PageDown', ' ', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']

const useScrollBehaviorBotCheck = (report: (action: TrackBotTrafficAction) => void): void => {
  const scrollSamples = useRef<ScrollSample[]>([])
  const scrollEventCount = useRef(0)
  const isTouchActive = useRef(false)
  const lastTouchEndMs = useRef(0)
  const isPointerDown = useRef(false)
  const lastScrollKeyMs = useRef(0)
  const lastFocusChangeMs = useRef(0)

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const nowMs = (): number => performance.now()

    // Where the page started, in pixels; used to tell whether it actually moved.
    const initialScrollPositionPx = window.scrollY

    // Seed with the starting position so the first real scroll gives a correct
    // step size for the large-jump check.
    scrollSamples.current = [{ positionPx: initialScrollPositionPx, timeMs: nowMs() }]

    // Wrap the app's own scroll methods so the jumps they cause are marked as
    // programmatic. The originals are restored on cleanup.
    const originalScrollTo = window.scrollTo.bind(window)
    const originalScrollBy = window.scrollBy.bind(window)

    window.scrollTo = ((...args: Parameters<typeof originalScrollTo>) => {
      markProgrammaticScroll()
      return originalScrollTo(...args)
    }) as typeof window.scrollTo

    window.scrollBy = ((...args: Parameters<typeof originalScrollBy>) => {
      markProgrammaticScroll()
      return originalScrollBy(...args)
    }) as typeof window.scrollBy

    // scrollIntoView is a separate API (on Element, not window), so wrap it too.
    const originalScrollIntoView = Element.prototype.scrollIntoView
    Element.prototype.scrollIntoView = function scrollIntoViewMarked(
      this: Element,
      ...args: Parameters<typeof originalScrollIntoView>
    ) {
      markProgrammaticScroll()
      return originalScrollIntoView.apply(this, args)
    } as typeof Element.prototype.scrollIntoView

    // A large jump is expected (not a bot) when the user caused it directly.
    const isUserDrivenJump = (): boolean => {
      return (
        isTouchActive.current ||
        nowMs() - lastTouchEndMs.current < FLING_SETTLE_MS ||
        isPointerDown.current ||
        nowMs() - lastScrollKeyMs.current < USER_JUMP_GRACE_MS ||
        nowMs() - lastFocusChangeMs.current < USER_JUMP_GRACE_MS
      )
    }

    // Only trusted touches count, so a fake touch cannot hide a programmatic jump.
    const handleTouchStart = (event: TouchEvent) => {
      if (event.isTrusted) {
        isTouchActive.current = true
      }
    }

    const handleTouchEnd = (event: TouchEvent) => {
      if (event.isTrusted) {
        isTouchActive.current = false
        lastTouchEndMs.current = nowMs()
      }
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (event.isTrusted) {
        isPointerDown.current = true
      }
    }

    const handlePointerUp = () => {
      isPointerDown.current = false
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.isTrusted && SCROLL_KEYS.includes(event.key)) {
        lastScrollKeyMs.current = nowMs()
      }
    }

    const handleFocusIn = () => {
      lastFocusChangeMs.current = nowMs()
    }

    // In-page anchor / hash-link jumps scroll natively (no scrollTo call), so mark
    // them as programmatic to exclude the jump they cause.
    const handleHashChange = () => {
      markProgrammaticScroll()
    }

    const handleAnchorClick = (event: MouseEvent) => {
      const target = event.target
      if (!(target instanceof Element)) {
        return
      }
      const anchor = target.closest('a[href]')
      const href = anchor?.getAttribute('href')
      if (href && href.startsWith('#')) {
        markProgrammaticScroll()
      }
    }

    const handleScroll = () => {
      const currentPositionPx = window.scrollY
      scrollEventCount.current += 1

      // A scroll the app triggered itself: start measuring fresh from here and
      // skip analysis, so its (possibly large) step is never counted.
      if (isProgrammaticScrollActive()) {
        scrollSamples.current = [{ positionPx: currentPositionPx, timeMs: nowMs() }]
        return
      }

      const samples = scrollSamples.current
      samples.push({ positionPx: currentPositionPx, timeMs: nowMs() })
      if (samples.length > MAX_SCROLL_SAMPLES) {
        samples.shift()
      }

      const isEvenScrolling = hasUniformScroll(samples)
      const isLargeJump = !isUserDrivenJump() && hasLargeScrollJump(samples)

      if (isEvenScrolling || isLargeJump) {
        report('scroll')
        // Start fresh from the current position so one detection does not keep
        // re-flagging on stale samples; later scrolls are judged anew.
        scrollSamples.current = [{ positionPx: currentPositionPx, timeMs: nowMs() }]
      }
    }

    window.addEventListener('touchstart', handleTouchStart, { passive: true, capture: true })
    window.addEventListener('touchend', handleTouchEnd, { passive: true, capture: true })
    window.addEventListener('pointerdown', handlePointerDown, { passive: true, capture: true })
    window.addEventListener('pointerup', handlePointerUp, { passive: true, capture: true })
    window.addEventListener('keydown', handleKeyDown, { passive: true, capture: true })
    window.addEventListener('focusin', handleFocusIn, { passive: true, capture: true })
    window.addEventListener('hashchange', handleHashChange)
    window.addEventListener('click', handleAnchorClick, { passive: true, capture: true })
    window.addEventListener('scroll', handleScroll, { passive: true })

    const watchTimer = window.setTimeout(() => {
      const scrollPositionChanged = window.scrollY !== initialScrollPositionPx

      // The page moved but no scroll event ever fired: a normal scroll always
      // fires an event, so this points to a bot setting the scroll position
      // directly. A page that simply stayed put is not flagged.
      if (scrollPositionChanged && scrollEventCount.current === 0) {
        report('scroll')
      }
    }, WATCH_DURATION_MS)

    return () => {
      window.removeEventListener('touchstart', handleTouchStart, { capture: true })
      window.removeEventListener('touchend', handleTouchEnd, { capture: true })
      window.removeEventListener('pointerdown', handlePointerDown, { capture: true })
      window.removeEventListener('pointerup', handlePointerUp, { capture: true })
      window.removeEventListener('keydown', handleKeyDown, { capture: true })
      window.removeEventListener('focusin', handleFocusIn, { capture: true })
      window.removeEventListener('hashchange', handleHashChange)
      window.removeEventListener('click', handleAnchorClick, { capture: true })
      window.removeEventListener('scroll', handleScroll)
      window.clearTimeout(watchTimer)
      window.scrollTo = originalScrollTo
      window.scrollBy = originalScrollBy
      Element.prototype.scrollIntoView = originalScrollIntoView
    }
  }, [])
}

export default useScrollBehaviorBotCheck
